import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { getSimulation, setSimulation as saveSimulation, clearSimulation as removeSimulation } from '../Helpers/localStorage';
import DataContext from './DataContext';

const SimulationContext = createContext();

export function SimulationProvider({ children, activeUser }) {
  const dataContext = useContext(DataContext);
  const history = useHistory();
  const [simulation, setSimulationState] = useState(() => {
    // Initialize from persisted simulation for super users so the initial render
    // matches stored state (avoids a flash of wrong role/org on page refresh)
    if (activeUser && activeUser.userType === 0) {
      const saved = getSimulation();
      if (saved) {
        const simulatedRole = saved.simulatedRole ?? null;
        const simulatedOrg = saved.simulatedOrg ?? null;
        return {
          isSimulating: simulatedRole !== null || simulatedOrg !== null,
          simulatedRole: simulatedRole,
          simulatedOrganization: simulatedOrg,
          actualRole: activeUser.userType,
          actualOrganization: activeUser.organization,
        };
      }
      return {
        isSimulating: false,
        simulatedRole: null,
        simulatedOrganization: null,
        actualRole: activeUser.userType,
        actualOrganization: activeUser.organization,
      };
    }
    // Non-super users or no active user: start with no simulation
    return {
      isSimulating: false,
      simulatedRole: null,
      simulatedOrganization: null,
      actualRole: activeUser?.userType ?? null,
      actualOrganization: activeUser?.organization ?? null,
    };
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load simulation from localStorage on mount, or clear state when user logs out
  // or is not a super user to prevent stale simulated state across sessions
  useEffect(() => {
    if (activeUser && activeUser.userType === 0) {
      const saved = getSimulation();
      if (saved) {
        const simulatedRole = saved.simulatedRole ?? null;
        const simulatedOrg = saved.simulatedOrg ?? null;
        setSimulationState({
          isSimulating: simulatedRole !== null || simulatedOrg !== null,
          simulatedRole: simulatedRole,
          simulatedOrganization: simulatedOrg,
          actualRole: activeUser.userType,
          actualOrganization: activeUser.organization,
        });
      } else {
        setSimulationState({
          isSimulating: false,
          simulatedRole: null,
          simulatedOrganization: null,
          actualRole: activeUser.userType,
          actualOrganization: activeUser.organization,
        });
      }
    } else {
      // User logged out or is not a super user — clear any lingering simulation state
      removeSimulation();
      setSimulationState({
        isSimulating: false,
        simulatedRole: null,
        simulatedOrganization: null,
        actualRole: activeUser?.userType ?? null,
        actualOrganization: activeUser?.organization ?? null,
      });
    }
  }, [activeUser]);

  // Update actual user info when activeUser changes
  useEffect(() => {
    if (activeUser) {
      setSimulationState(prev => ({
        ...prev,
        actualRole: activeUser.userType,
        actualOrganization: activeUser.organization,
      }));
    }
  }, [activeUser?.userType, activeUser?.organization]);

  const refreshData = useCallback(async () => {
    // When organization changes, we clear the active project
    // The pages will handle redirecting to project selector if needed
    setIsRefreshing(true);
    try {
      // Just wait a moment for state to settle
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (err) {
      console.error('Error during simulation change:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  const setSimulatedRole = useCallback(async (role) => {
    setSimulationState(prev => {
      const next = {
        ...prev,
        simulatedRole: role,
        isSimulating: role !== null || prev.simulatedOrganization !== null,
      };
      saveSimulation(next.simulatedRole, next.simulatedOrganization);
      return next;
    });

    // Clear or refresh active project data when role changes, similar to org changes
    if (dataContext) {
      if (typeof dataContext.refreshActiveProject === 'function') {
        await dataContext.refreshActiveProject();
      } else if (typeof dataContext.setActiveProject === 'function') {
        dataContext.setActiveProject(null);
      }
    }

    // Trigger data refresh
    await refreshData();
  }, [refreshData, dataContext]);

  const setSimulatedOrganization = useCallback(async (org) => {
    // Clear active project when changing organizations
    const cookies = new Cookies(null, { path: '/' });
    cookies.remove('activeProjectId', { path: '/' });

    setSimulationState(prev => {
      const next = {
        ...prev,
        simulatedOrganization: org,
        isSimulating: prev.simulatedRole !== null || org !== null,
      };
      saveSimulation(next.simulatedRole, next.simulatedOrganization);
      return next;
    });

    // Clear active project data in DataContext using public APIs
    if (dataContext) {
      if (typeof dataContext.refreshActiveProject === 'function') {
        await dataContext.refreshActiveProject();
      } else if (typeof dataContext.setActiveProject === 'function') {
        dataContext.setActiveProject(null);
      }
    }

    // Trigger data refresh
    await refreshData();
  }, [refreshData, dataContext]);

  const clearSimulation = useCallback(async () => {
    // Clear active project cookie when exiting simulation
    const cookies = new Cookies(null, { path: '/' });
    cookies.remove('activeProjectId', { path: '/' });
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('SimulationContext: Clearing simulation and active project');
    }
    
    setSimulationState({
      isSimulating: false,
      simulatedRole: null,
      simulatedOrganization: null,
      actualRole: activeUser?.userType ?? null,
      actualOrganization: activeUser?.organization ?? null,
    });
    removeSimulation();
    
    // Clear active project data in DataContext
    if (dataContext) {
      if (typeof dataContext.refreshActiveProject === 'function') {
        await dataContext.refreshActiveProject();
      } else if (typeof dataContext.setActiveProject === 'function') {
        dataContext.setActiveProject(null);
      }
    }
    
    // Trigger data refresh
    await refreshData();
    
    // Navigate to dashboard (project selector) after clearing simulation
    // This ensures user is taken to a safe state regardless of where they were
    if (history) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('SimulationContext: Navigating to dashboard after clearing simulation');
      }
      history.push('/dashboard');
    }
  }, [activeUser, refreshData, dataContext, history]);

  const getEffectiveRole = useCallback(() => {
    return simulation.simulatedRole !== null ? simulation.simulatedRole : simulation.actualRole;
  }, [simulation]);

  const getEffectiveOrganization = useCallback(() => {
    return simulation.simulatedOrganization || simulation.actualOrganization;
  }, [simulation]);

  const value = {
    ...simulation,
    isRefreshing,
    setSimulatedRole,
    setSimulatedOrganization,
    clearSimulation,
    getEffectiveRole,
    getEffectiveOrganization,
  };

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

const DEFAULT_SIMULATION = {
  isSimulating: false,
  simulatedRole: null,
  simulatedOrganization: null,
  actualRole: null,
  actualOrganization: null,
  isRefreshing: false,
  setSimulatedRole: async () => {},
  setSimulatedOrganization: async () => {},
  clearSimulation: async () => {},
  getEffectiveRole: () => null,
  getEffectiveOrganization: () => null,
};

export const useSimulation = () => {
  const context = useContext(SimulationContext);
  return context || DEFAULT_SIMULATION;
};

export default SimulationContext;
